'use client';

import * as React from 'react';
import { useTranslations } from 'next-intl';
import { GripVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PanelResizeHandleProps } from '@/hooks/use-panel-width';

/**
 * The drag affordance for a resizable side panel. Purely presentational —
 * every behaviour arrives spread in from `usePanelWidth().handleProps`.
 *
 * It used to render as a bare 4px strip that drew nothing until the cursor
 * was already on it, so the panels read as fixed-width: an operator squeezed
 * between a 420px panel and a dense Arabic scan had no way to learn the seam
 * could move. Three things make it legible now, and all three are painted by
 * elements that do NOT affect layout — the strip keeps its `w-1` footprint,
 * because `buildScanGridTemplate` sizes its column at `RESIZE_HANDLE_PX` (4)
 * and a wider box would overflow that column onto the page beside it.
 *
 * 1. A seam that is always drawn, not only on hover, so the boundary reads as
 *    a control rather than a gap.
 * 2. A grip, centred in the seam. It is the one element a reader recognises
 *    without hovering, which is the whole point.
 * 3. A hit area wider than the seam (`before`, transparent, ±4px), so the
 *    drag starts where the cursor changes shape instead of a pixel later.
 *
 * Renders `hidden lg:flex`: below `lg` every panel this attaches to is
 * full-width or inside a Sheet, where there is no width to drag.
 */
export function PanelResizeHandle({
  className,
  ...props
}: PanelResizeHandleProps & { className?: string }) {
  const t = useTranslations('common');

  return (
    <div
      role='separator'
      aria-orientation='vertical'
      aria-label={t('resizePanel')}
      title={t('resizePanelHint')}
      tabIndex={0}
      data-slot='panel-resize-handle'
      className={cn(
        'group/resize relative z-20 hidden w-1 shrink-0 cursor-col-resize touch-none outline-none select-none lg:flex',
        // The hit area, invisible and wider than the seam. A 4px target is
        // one the cursor skates past; this is the standard ~12px.
        'before:absolute before:-inset-x-1 before:inset-y-0 before:content-[""]',
        // The seam itself: a 1px line at rest that thickens and takes the
        // accent while the panel is being dragged. Physical centring is
        // direction-neutral for a symmetric element, so this needs no RTL
        // branch — and it matches how SidebarRail draws its own line.
        'after:bg-border/80 after:absolute after:inset-y-0 after:left-1/2 after:w-px after:-translate-x-1/2 after:transition-[background-color,width] after:duration-150',
        'hover:after:bg-primary/60 hover:after:w-[3px]',
        'focus-visible:after:bg-ring focus-visible:after:w-[3px]',
        'data-[resizing]:after:bg-primary data-[resizing]:after:w-[3px]',
        className
      )}
      {...props}
    >
      {/* Deliberately NOT `pointer-events-none`: the grip overhangs the 4px
          strip, and a reader who aims at the one visible mark has to be able
          to drag from it. It is a child of the handle, so its events bubble
          into the same pointer handlers. */}
      <span
        aria-hidden='true'
        className={cn(
          'bg-background border-border absolute top-1/2 left-1/2 flex h-10 w-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border opacity-80 shadow-sm',
          'transition-[opacity,color,background-color,border-color,box-shadow] duration-150 ease-out',
          'group-hover/resize:border-primary/50 group-hover/resize:opacity-100 group-hover/resize:shadow-md',
          'group-focus-visible/resize:border-ring group-focus-visible/resize:opacity-100',
          'group-data-[resizing]/resize:bg-primary group-data-[resizing]/resize:border-primary group-data-[resizing]/resize:opacity-100'
        )}
      >
        <GripVertical
          className={cn(
            'text-muted-foreground h-3.5 w-3.5 transition-colors duration-150',
            'group-hover/resize:text-primary',
            'group-data-[resizing]/resize:text-primary-foreground'
          )}
        />
      </span>
    </div>
  );
}
